import { useEffect, useMemo, useRef, useState } from "react";

import {
  acknowledgeDriverFleetOrderAmendment as acknowledgeDriverFleetOrderAmendmentRequest,
  completeDriverStop as completeDriverStopRequest,
  continueDriverTrip,
  confirmDriverLoading as confirmDriverLoadingRequest,
  discardDriverTaskRouteDraft,
  failDriverStop as failDriverStopRequest,
  getDriverProfile,
  getDriverTaskDetail,
  getDriverTaskMap,
  getDriverRouteWorkspace,
  getDriverTasks,
  optimizeDriverTaskRoute,
  previewDriverTaskRoute,
  saveDriverTaskRoute,
  loginDriver,
  logoutDriver,
  makeDriverStopNext as makeDriverStopNextRequest,
  saveDriverLoadingChecks as saveDriverLoadingChecksRequest,
  startDriverStop as startDriverStopRequest,
} from "../api/client.js";
import { Icon } from "../components/Icon.jsx";
import {
  DriverExpensesPage,
  DriverIncidentSummary,
  DriverNotificationsPage,
  DriverVehiclePage,
} from "./DriverOperations.jsx";
import { DriverDeliveryReportWizard } from "./DriverDeliveryReports.jsx";
import { DriverRouteMap } from "./DriverRouteMap.jsx";
import { DriverRouteWorkspace } from "./DriverRouteWorkspace.jsx";
import { DriverSyncRecovery } from "./DriverSyncRecovery.jsx";
import { DriverTaskDetail } from "./DriverTaskDetail.jsx";
import { notifyDriver } from "./driver-notifications.js";
import {
  driverQueueStatus,
  flushDriverQueue,
  initializeDriverOfflineStore,
  subscribeDriverSync,
  subscribeDriverSessionRecovery,
} from "./offline-store.js";
import {
  activateDriverPwaUpdate,
  subscribeDriverPwaUpdate,
} from "./driver-pwa.js";

const DRIVER_NAVIGATION = [
  { id: "tasks", label: "Tugas", icon: "orders" },
  { id: "expenses", label: "Biaya", icon: "wallet" },
  { id: "vehicles", label: "Armada", icon: "truck" },
  { id: "notifications", label: "Notifikasi", icon: "bell" },
  { id: "profile", label: "Profil", icon: "user" },
];

const TASK_TABS = [
  { id: "running", label: "Sedang Berjalan" },
  { id: "upcoming", label: "Berikutnya" },
  { id: "completed", label: "Selesai" },
];

function todayLabel() {
  return new Intl.DateTimeFormat("id-ID", {
    timeZone: "Asia/Jakarta",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
}

function statusTone(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "IN_PROGRESS" || normalized === "EN_ROUTE") return "active";
  if (normalized === "COMPLETED" || normalized === "COMPLETED_WITH_ISSUE") return "success";
  if (normalized === "FAILED" || normalized === "CANCELLED") return "danger";
  if (normalized === "DEFERRED") return "warning";
  return "neutral";
}

function statusIcon(status) {
  const normalized = String(status || "PENDING").toUpperCase();
  if (normalized === "COMPLETED") return "check";
  if (normalized === "COMPLETED_WITH_ISSUE") return "warning";
  if (normalized === "EN_ROUTE") return "route";
  if (normalized === "FAILED" || normalized === "CANCELLED") return "close";
  if (normalized === "DEFERRED") return "clock";
  return null;
}

function stopStatusLabel(status) {
  const normalized = String(status || "PENDING").toUpperCase();
  if (normalized === "COMPLETED") return "Selesai";
  if (normalized === "COMPLETED_WITH_ISSUE") return "Ada kendala";
  if (normalized === "EN_ROUTE") return "Sedang menuju";
  if (normalized === "FAILED") return "Gagal";
  if (normalized === "CANCELLED") return "Dibatalkan";
  if (normalized === "DEFERRED") return "Ditunda";
  return "Belum dijalankan";
}

function vehicleLabel(task) {
  return [task.vehiclePlate, task.vehicleType].filter(Boolean).join(" · ") || "Belum ditentukan";
}

function applyQueuedRouteDraftToDetail(detail, draft) {
  if (!detail?.stops?.length || !draft?.stopKeys?.length) return detail;
  const editableOrder = new Map(draft.stopKeys.map((key, index) => [String(key), index]));
  const prefix = detail.stops.filter((stop) => {
    const status = String(stop.status || "PENDING").toUpperCase();
    return status === "EN_ROUTE" || Boolean(stop.actualSequence || stop.startedAt || stop.completedAt);
  });
  const prefixKeys = new Set(prefix.map((stop) => String(stop.key)));
  const editable = draft.stopKeys
    .map((key) => detail.stops.find((stop) => String(stop.key) === String(key)))
    .filter(Boolean);
  const suffix = detail.stops.filter((stop) => !prefixKeys.has(String(stop.key)) && !editableOrder.has(String(stop.key)));
  const nextStops = [...prefix, ...editable, ...suffix].map((stop, index) => ({ ...stop, sequence: index + 1 }));
  return {
    ...detail,
    stops: nextStops,
    execution: {
      ...detail.execution,
      routeVersion: Math.max(
        Number(detail.execution?.routeVersion || 1),
        Number(draft.expectedRouteVersion || 1) + 1,
      ),
      routeSyncPending: true,
    },
  };
}

export default function DriverApp() {
  const [authState, setAuthState] = useState("loading");
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState("");
  const [activePage, setActivePage] = useState("tasks");
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [mapPayload, setMapPayload] = useState(null);
  const [routeWorkspacePayload, setRouteWorkspacePayload] = useState(null);
  const [routeWorkspaceLoading, setRouteWorkspaceLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");
  const [quickReportStage, setQuickReportStage] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncState, setSyncState] = useState({ pending: 0, failed: 0, mediaPending: 0, mediaFailed: 0, syncing: false, nextRetryAt: null });
  const [updateRegistration, setUpdateRegistration] = useState(null);
  const [sessionRetryCount, setSessionRetryCount] = useState(0);
  const systemBannerStackRef = useRef(null);
  const offlineStorePromiseRef = useRef(null);
  const historyReadyRef = useRef(false);
  const activePageRef = useRef("tasks");
  const selectedTaskIdRef = useRef(null);
  const mapOpenRef = useRef(false);
  const routeWorkspaceOpenRef = useRef(false);
  const routeWorkspaceDirtyRef = useRef(false);
  const routeWorkspaceExitRequestRef = useRef(null);
  const routeWorkspaceExitBypassRef = useRef(false);
  const exitArmedUntilRef = useRef(0);
  const exitRearmTimerRef = useRef(null);

  function ensureOfflineStoreReady() {
    if (!offlineStorePromiseRef.current) {
      offlineStorePromiseRef.current = initializeDriverOfflineStore().catch((error) => {
        offlineStorePromiseRef.current = null;
        throw error;
      });
    }
    return offlineStorePromiseRef.current;
  }

  function driverUrl({ page = "tasks", taskId = null } = {}) {
    const url = new URL(window.location.href);
    url.searchParams.delete("page");
    url.searchParams.delete("task");
    if (page && page !== "tasks") url.searchParams.set("page", page);
    if (taskId) url.searchParams.set("task", taskId);
    return `${url.pathname}${url.search}`;
  }

  function clearSelectedTask() {
    setSelectedTaskId(null);
    setTaskDetail(null);
    setDetailError("");
    setMapPayload(null);
    setRouteWorkspacePayload(null);
    setQuickReportStage(null);
    routeWorkspaceDirtyRef.current = false;
    routeWorkspaceExitRequestRef.current = null;
  }

  useEffect(() => {
    restoreDriverSession({ resetRetries: true });
  }, []);


  useEffect(() => {
    activePageRef.current = activePage;
    selectedTaskIdRef.current = selectedTaskId;
    mapOpenRef.current = Boolean(mapPayload);
    routeWorkspaceOpenRef.current = Boolean(routeWorkspacePayload);
  }, [activePage, mapPayload, routeWorkspacePayload, selectedTaskId]);


  useEffect(() => {
    if (authState !== "error" || !online || sessionRetryCount >= 2) return undefined;
    const timer = window.setTimeout(() => {
      setSessionRetryCount((count) => count + 1);
      restoreDriverSession();
    }, 2_500);
    return () => window.clearTimeout(timer);
  }, [authState, online, sessionRetryCount]);

  useEffect(() => {
    let mounted = true;
    async function refreshQueue() {
      const status = await driverQueueStatus();
      if (mounted) setSyncState((current) => ({ ...current, ...status }));
      if (mounted && navigator.onLine && routeWorkspaceOpenRef.current && !routeWorkspaceDirtyRef.current && selectedTaskIdRef.current) {
        getDriverRouteWorkspace(selectedTaskIdRef.current)
          .then((payload) => { if (mounted) setRouteWorkspacePayload(payload); })
          .catch(() => {});
      }
      if (mounted && navigator.onLine && selectedTaskIdRef.current && !routeWorkspaceDirtyRef.current) {
        getDriverTaskDetail(selectedTaskIdRef.current)
          .then((payload) => { if (mounted) setTaskDetail(payload); })
          .catch(() => {});
      }
    }
    function handleOnline() { setOnline(true); }
    function handleOffline() { setOnline(false); }
    const unsubscribe = subscribeDriverSync(refreshQueue);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    ensureOfflineStoreReady()
      .then(refreshQueue)
      .catch(() => refreshQueue());
    return () => {
      mounted = false;
      unsubscribe();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!online || authState !== "authenticated") return undefined;
    let cancelled = false;
    setSyncState((current) => ({ ...current, syncing: true }));
    (async () => {
      try {
        await ensureOfflineStoreReady();
        await flushDriverQueue({ includeRecoverable: true, source: "authenticated-online" });
        const status = await driverQueueStatus();
        if (!cancelled) setSyncState((current) => ({ ...current, ...status }));
        await loadTasks();
      } catch {
        const status = await driverQueueStatus().catch(() => null);
        if (!cancelled && status) setSyncState((current) => ({ ...current, ...status }));
      } finally {
        if (!cancelled) setSyncState((current) => ({ ...current, syncing: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [authState, online]);

  useEffect(() => subscribeDriverPwaUpdate(setUpdateRegistration), []);

  useEffect(() => {
    const root = document.documentElement;
    const stack = systemBannerStackRef.current;
    if (!stack) return undefined;

    function syncSystemBannerOffset() {
      const height = Math.ceil(stack.getBoundingClientRect().height || 0);
      root.style.setProperty("--driver-system-banner-height", `${height}px`);
      root.classList.toggle("driver-system-banner-active", height > 1);
      root.classList.toggle("driver-pwa-update-ready", Boolean(updateRegistration));
    }

    syncSystemBannerOffset();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(syncSystemBannerOffset) : null;
    observer?.observe(stack);
    window.addEventListener("resize", syncSystemBannerOffset);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", syncSystemBannerOffset);
      root.style.removeProperty("--driver-system-banner-height");
      root.classList.remove("driver-system-banner-active", "driver-pwa-update-ready");
    };
  }, [authState, updateRegistration, syncState.failed, syncState.mediaFailed, syncState.mediaPending, syncState.pending, syncState.syncing]);

  useEffect(() => subscribeDriverSessionRecovery(() => {
    restoreDriverSession({ resetRetries: true });
  }), []);

  useEffect(() => {
    if (!online || authState !== "authenticated" || !syncState.nextRetryAt || syncState.syncing) return undefined;
    const remaining = Math.max(500, new Date(syncState.nextRetryAt).getTime() - Date.now());
    const timer = window.setTimeout(async () => {
      setSyncState((current) => ({ ...current, syncing: true }));
      try {
        await flushDriverQueue({ source: "scheduled" });
        const status = await driverQueueStatus();
        setSyncState((current) => ({ ...current, ...status }));
      } finally {
        setSyncState((current) => ({ ...current, syncing: false }));
      }
    }, Math.min(remaining, 5 * 60_000));
    return () => window.clearTimeout(timer);
  }, [authState, online, syncState.nextRetryAt, syncState.syncing]);

  async function refreshSyncStatus() {
    const status = await driverQueueStatus();
    setSyncState((current) => ({ ...current, ...status }));
  }

  async function restoreDriverSession({ resetRetries = false } = {}) {
    if (resetRetries) setSessionRetryCount(0);
    setAuthState("loading");
    try {
      const payload = await getDriverProfile();
      setUser(payload.user);
      setAuthState("authenticated");
    } catch (error) {
      if (error.status === 401) {
        setUser(null);
        setAuthState("anonymous");
        return;
      }
      setAuthState("error");
    }
  }

  useEffect(() => {
    if (authState !== "authenticated") return;
    loadTasks();
  }, [authState]);

  useEffect(() => {
    if (authState !== "authenticated" || historyReadyRef.current) return;

    const currentUrl = new URL(window.location.href);
    const initialTaskId = currentUrl.searchParams.get("task");
    const requestedPage = currentUrl.searchParams.get("page") || "tasks";
    const initialPage = DRIVER_NAVIGATION.some((item) => item.id === requestedPage) ? requestedPage : "tasks";

    window.history.replaceState(
      { driverApp: true, driverView: "root", driverPage: initialPage },
      "",
      driverUrl({ page: initialPage }),
    );
    window.history.pushState(
      { driverApp: true, driverView: "page", driverPage: initialPage },
      "",
      driverUrl({ page: initialPage }),
    );
    historyReadyRef.current = true;
    setActivePage(initialPage);

    if (initialTaskId) openTask(initialTaskId);
  }, [authState]);

  useEffect(() => {
    if (authState !== "authenticated") return undefined;

    function handleHistoryChange(event) {
      const state = event.state || {};
      if (!state.driverApp) return;

      if (
        routeWorkspaceOpenRef.current
        && routeWorkspaceDirtyRef.current
        && !routeWorkspaceExitBypassRef.current
        && state.driverView !== "route-workspace"
      ) {
        window.setTimeout(() => {
          const taskId = selectedTaskIdRef.current;
          window.history.pushState(
            { driverApp: true, driverView: "route-workspace", driverPage: "tasks", driverTaskId: taskId },
            "",
            driverUrl({ page: "tasks", taskId }),
          );
          routeWorkspaceExitRequestRef.current?.();
        }, 0);
        return;
      }

      if (routeWorkspaceExitBypassRef.current) {
        routeWorkspaceExitBypassRef.current = false;
      }

      if (state.driverView === "root") {
        const now = Date.now();
        if (now <= exitArmedUntilRef.current) {
          exitArmedUntilRef.current = 0;
          if (exitRearmTimerRef.current) {
            window.clearTimeout(exitRearmTimerRef.current);
            exitRearmTimerRef.current = null;
          }
          window.history.back();
          return;
        }

        exitArmedUntilRef.current = now + 2200;
        notifyDriver("Tekan kembali sekali lagi untuk keluar", {
          id: "driver-exit-confirm",
          tone: "info",
          duration: 2200,
          compact: true,
          dismissible: false,
          icon: "chevronLeft",
        });

        // Tunggu gesture/predictive-back Android selesai sebelum memasang ulang
        // guard history. pushState yang dilakukan sinkron saat popstate membuat
        // halaman terlihat bergeser lalu memantul kembali.
        exitRearmTimerRef.current = window.setTimeout(() => {
          exitRearmTimerRef.current = null;
          if (window.history.state?.driverApp && window.history.state?.driverView === "root") {
            const page = activePageRef.current || "tasks";
            window.history.pushState(
              { driverApp: true, driverView: "page", driverPage: page },
              "",
              driverUrl({ page }),
            );
          }
        }, 180);
        return;
      }

      exitArmedUntilRef.current = 0;
      const routeWorkspaceWasOpen = routeWorkspaceOpenRef.current;
      if (state.driverView !== "map") setMapPayload(null);
      if (state.driverView !== "route-workspace") {
        setRouteWorkspacePayload(null);
        if (routeWorkspaceWasOpen) {
          window.requestAnimationFrame(() => document.querySelector(".driver-route-floating-action")?.focus({ preventScroll: true }));
        }
      }

      const url = new URL(window.location.href);
      const nextTaskId = url.searchParams.get("task");
      const requestedPage = state.driverPage || url.searchParams.get("page") || "tasks";
      const nextPage = DRIVER_NAVIGATION.some((item) => item.id === requestedPage) ? requestedPage : "tasks";
      setActivePage(nextPage);

      if (nextTaskId && nextTaskId !== selectedTaskIdRef.current) {
        openTask(nextTaskId, { updateHistory: false });
      } else if (!nextTaskId && selectedTaskIdRef.current) {
        clearSelectedTask();
      }
    }

    window.addEventListener("popstate", handleHistoryChange);
    return () => {
      window.removeEventListener("popstate", handleHistoryChange);
      if (exitRearmTimerRef.current) {
        window.clearTimeout(exitRearmTimerRef.current);
        exitRearmTimerRef.current = null;
      }
    };
  }, [authState]);

  useEffect(() => {
    document.title = taskDetail?.task?.foNumber
      ? `${taskDetail.task.foNumber} · Sedulur Dalan`
      : "Sedulur Dalan";

    return () => {
      document.title = "Route Planning";
    };
  }, [taskDetail]);

  async function loadTasks() {
    setTasksLoading(true);
    setTasksError("");

    try {
      const payload = await getDriverTasks();
      setTasks(payload.tasks || []);
    } catch (error) {
      if (error.status === 401) {
        setUser(null);
        setAuthState("anonymous");
      } else {
        setTasksError(error.message);
      }
    } finally {
      setTasksLoading(false);
    }
  }

  async function openTask(taskId, { updateHistory = true } = {}) {
    if (updateHistory) {
      window.history.pushState(
        { driverApp: true, driverView: "task", driverPage: "tasks", driverTaskId: taskId },
        "",
        driverUrl({ page: "tasks", taskId }),
      );
    }
    setSelectedTaskId(taskId);
    setTaskDetail(null);
    setDetailError("");
    setDetailLoading(true);
    window.scrollTo({ top: 0, behavior: "smooth" });

    try {
      const payload = await getDriverTaskDetail(taskId);
      setTaskDetail(payload);
    } catch (error) {
      setDetailError(error.message);
    } finally {
      setDetailLoading(false);
    }
  }

  function closeTask() {
    if (window.history.state?.driverApp && new Set(["task", "map"]).has(window.history.state?.driverView)) {
      window.history.back();
      return;
    }
    clearSelectedTask();
    window.history.replaceState(
      { driverApp: true, driverView: "page", driverPage: "tasks" },
      "",
      driverUrl({ page: "tasks" }),
    );
  }

  async function openRouteWorkspace() {
    if (!selectedTaskId) return;
    setRouteWorkspaceLoading(true);
    try {
      const payload = await getDriverRouteWorkspace(selectedTaskId);
      if (window.history.state?.driverView !== "route-workspace") {
        window.history.pushState(
          { driverApp: true, driverView: "route-workspace", driverPage: "tasks", driverTaskId: selectedTaskId },
          "",
          driverUrl({ page: "tasks", taskId: selectedTaskId }),
        );
      }
      setRouteWorkspacePayload(payload);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
      });
    } catch (error) {
      notifyDriver(error.message || "Pengaturan rute belum dapat dibuka.", { tone: "error", title: "Rute belum tersedia" });
    } finally {
      setRouteWorkspaceLoading(false);
    }
  }

  function closeRouteWorkspace({ force = false } = {}) {
    if (routeWorkspaceDirtyRef.current && !force) {
      routeWorkspaceExitRequestRef.current?.();
      return;
    }
    if (force) routeWorkspaceExitBypassRef.current = true;
    routeWorkspaceDirtyRef.current = false;
    routeWorkspaceExitRequestRef.current = null;
    if (window.history.state?.driverApp && window.history.state?.driverView === "route-workspace") {
      window.history.back();
      return;
    }
    setRouteWorkspacePayload(null);
    window.requestAnimationFrame(() => document.querySelector(".driver-route-floating-action")?.focus({ preventScroll: true }));
  }

  async function reloadRouteWorkspace() {
    if (!selectedTaskId) return;
    const payload = await getDriverRouteWorkspace(selectedTaskId);
    setRouteWorkspacePayload(payload);
    return payload;
  }

  async function optimizeRouteWorkspace() {
    if (!selectedTaskId) return null;
    return optimizeDriverTaskRoute(selectedTaskId);
  }

  async function previewRouteWorkspace(stopKeys) {
    if (!selectedTaskId) return null;
    return previewDriverTaskRoute(selectedTaskId, stopKeys);
  }

  async function saveRouteWorkspace(payload) {
    if (!selectedTaskId) return null;
    setActionBusy(true);
    try {
      const nextWorkspace = await saveDriverTaskRoute(selectedTaskId, payload);
      if (nextWorkspace?.queued) {
        setRouteWorkspacePayload((current) => ({ ...current, routeDraft: nextWorkspace.routeDraft }));
        setTaskDetail((current) => applyQueuedRouteDraftToDetail(current, nextWorkspace.routeDraft));
        await refreshSyncStatus();
        notifyDriver("Urutan rute tersimpan di perangkat dan menunggu sinkronisasi.", { tone: "warning", title: "Data aman di perangkat" });
        return nextWorkspace;
      }
      setRouteWorkspacePayload(nextWorkspace);
      const nextDetail = await getDriverTaskDetail(selectedTaskId);
      setTaskDetail(nextDetail);
      await loadTasks();
      notifyDriver("Urutan rute berhasil disimpan.", { tone: "success" });
      return nextWorkspace;
    } finally {
      setActionBusy(false);
    }
  }

  async function discardRouteWorkspaceDraft() {
    if (!selectedTaskId) return null;
    setActionBusy(true);
    try {
      const nextWorkspace = await discardDriverTaskRouteDraft(selectedTaskId);
      setRouteWorkspacePayload(nextWorkspace);
      await refreshSyncStatus();
      notifyDriver("Draft lokal dibuang. Rute terbaru sudah dimuat.", { tone: "info" });
      return nextWorkspace;
    } finally {
      setActionBusy(false);
    }
  }

  async function openMap() {
    if (!selectedTaskId) return;
    setMapLoading(true);
    setMapError("");

    try {
      const payload = await getDriverTaskMap(selectedTaskId);
      if (window.history.state?.driverView !== "map") {
        window.history.pushState(
          { driverApp: true, driverView: "map", driverPage: "tasks", driverTaskId: selectedTaskId },
          "",
          driverUrl({ page: "tasks", taskId: selectedTaskId }),
        );
      }
      setMapPayload(payload);
    } catch (error) {
      setMapError(error.message);
      notifyDriver(error.message, { tone: "error", title: "Peta belum dapat dibuka" });
    } finally {
      setMapLoading(false);
    }
  }

  function closeMap() {
    if (window.history.state?.driverApp && window.history.state?.driverView === "map") {
      window.history.back();
      return;
    }
    setMapPayload(null);
  }

  async function refreshSelectedTask({ refreshMap = false, scrollTop = false } = {}) {
    if (!selectedTaskId) return;
    const [detailPayload, nextMap] = await Promise.all([
      getDriverTaskDetail(selectedTaskId),
      refreshMap ? getDriverTaskMap(selectedTaskId) : Promise.resolve(null),
    ]);
    setTaskDetail(detailPayload);
    if (nextMap) setMapPayload(nextMap);
    await loadTasks();
    if (scrollTop) {
      requestAnimationFrame(() => {
        const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
        document.getElementById("driver-content")?.focus({ preventScroll: true });
      });
    }
  }

  async function performTaskAction(action, warning = "", options = {}) {
    setActionBusy(true);
    try {
      const payload = await action();
      if (payload?.task) setTaskDetail(payload);
      if (options.refreshDetail && selectedTaskId) {
        const refreshedDetail = await getDriverTaskDetail(selectedTaskId);
        setTaskDetail(refreshedDetail);
      }
      await loadTasks();
      if (options.refreshMap && mapOpenRef.current && selectedTaskId) {
        const nextMap = await getDriverTaskMap(selectedTaskId);
        setMapPayload(nextMap);
      }
      if (warning) notifyDriver(warning, { tone: "warning" });
      if (options.successMessage) notifyDriver(options.successMessage, { tone: "success" });
      if (options.scrollTop) {
        requestAnimationFrame(() => {
          const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
          window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
          document.getElementById("driver-content")?.focus({ preventScroll: true });
        });
      }
      return payload;
    } catch (error) {
      notifyDriver(error.message || "Tindakan belum dapat diproses.", {
        tone: "error",
        title: options.errorTitle || "Tindakan gagal",
        duration: 7000,
      });
      throw error;
    } finally {
      setActionBusy(false);
    }
  }

  const driverActions = {
    saveLoading: (checks) => performTaskAction(
      () => saveDriverLoadingChecksRequest(selectedTaskId, checks),
      "",
      { successMessage: "Checklist loading tersimpan." },
    ),
    confirmLoading: (payload = {}) => performTaskAction(
      () => confirmDriverLoadingRequest(selectedTaskId, payload),
      "",
      { successMessage: "Loading selesai dikonfirmasi.", scrollTop: true },
    ),
    acknowledgeAmendment: (amendmentId) => performTaskAction(
      () => acknowledgeDriverFleetOrderAmendmentRequest(selectedTaskId, amendmentId),
      "",
      { successMessage: "Perubahan Fleet Order telah diterima." },
    ),
    continueTrip: () => performTaskAction(
      () => continueDriverTrip(selectedTaskId, {}),
      "",
      { refreshDetail: true, successMessage: "Hari berikutnya dimulai. Pengiriman dapat dilanjutkan." },
    ),
    startStop: (stopKey, location, warning) => performTaskAction(() => startDriverStopRequest(selectedTaskId, { stopKey, ...location }), warning, { refreshMap: true, successMessage: "Tujuan aktif diperbarui. Mulai menuju lokasi." }),
    makeNext: (stopKey) => performTaskAction(() => makeDriverStopNextRequest(selectedTaskId, { stopKey }), "", { refreshMap: true, successMessage: "Tujuan berikutnya berhasil diperbarui." }),
    completeStop: (stopKey, payload, warning) => performTaskAction(
      () => completeDriverStopRequest(selectedTaskId, { stopKey, ...payload }),
      warning,
      { refreshMap: true, successMessage: "Titik selesai. Foto bukti telah tersinkron dan tersimpan." },
    ),
    failStop: (stopKey, payload, warning) => performTaskAction(() => failDriverStopRequest(selectedTaskId, { stopKey, ...payload }), warning, { refreshMap: true, successMessage: "Hasil titik disimpan." }),
  };

  const activeSyncRepairContext = useMemo(() => {
    const activeStop = taskDetail?.stops?.find((stop) => stop.status === "EN_ROUTE");
    const fleetOrderId = taskDetail?.task?.id || selectedTaskId;
    if (!fleetOrderId || !activeStop?.key) return null;
    return {
      fleetOrderId,
      stopKey: activeStop.key,
      foNumber: taskDetail?.task?.foNumber || null,
      customerName: activeStop.customerName || null,
      ownerType: "DELIVERY_STOP",
    };
  }, [selectedTaskId, taskDetail]);

  function reportStageForQuickShare() {
    if (!taskDetail?.deliveryReporting?.available) return null;
    if (taskDetail.deliveryReporting.morningRequired) return "MORNING";
    if (taskDetail.deliveryReporting.allStopsFinal && !taskDetail.deliveryReporting.finalTrip?.satisfied) return "FINAL_TRIP";
    return "STATUS_UPDATE";
  }

  function openQuickReport() {
    const stage = reportStageForQuickShare();
    if (stage) setQuickReportStage(stage);
  }

  async function handleLogout() {
    try {
      await logoutDriver();
    } finally {
      setUser(null);
      setTasks([]);
      setSelectedTaskId(null);
      setTaskDetail(null);
      setActivePage("tasks");
      historyReadyRef.current = false;
      window.history.replaceState({}, "", "/sedulurdalan/");
      setAuthState("anonymous");
    }
  }


  function navigate(page) {
    if (page !== activePageRef.current || selectedTaskIdRef.current || mapOpenRef.current) {
      window.history.pushState(
        { driverApp: true, driverView: "page", driverPage: page },
        "",
        driverUrl({ page }),
      );
    }
    clearSelectedTask();
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (authState === "loading") return <DriverLoadingScreen />;
  if (authState === "error") return <DriverFatalScreen onRetry={() => restoreDriverSession({ resetRetries: true })} />;
  if (authState === "anonymous") {
    return (
      <DriverLogin
        onLogin={(loggedInUser) => {
          setUser(loggedInUser);
          setAuthState("authenticated");
        }}
      />
    );
  }

  if (routeWorkspacePayload) {
    return (
      <DriverRouteWorkspace
        actionBusy={actionBusy}
        onClose={closeRouteWorkspace}
        onDiscardDraft={discardRouteWorkspaceDraft}
        onDirtyChange={(dirty) => { routeWorkspaceDirtyRef.current = Boolean(dirty); }}
        onOptimize={optimizeRouteWorkspace}
        onPreview={previewRouteWorkspace}
        onRegisterExitRequest={(handler) => { routeWorkspaceExitRequestRef.current = handler; }}
        onReload={reloadRouteWorkspace}
        onSave={saveRouteWorkspace}
        online={online}
        payload={routeWorkspacePayload}
      />
    );
  }

  if (mapPayload) {
    return (
      <>
        <DriverRouteMap
          actionBusy={actionBusy}
          online={online}
          onClose={closeMap}
          onCompleteStop={driverActions.completeStop}
          onFailStop={driverActions.failStop}
          onMakeNext={driverActions.makeNext}
          onShareReport={taskDetail?.deliveryReporting?.available ? openQuickReport : null}
          onStartStop={driverActions.startStop}
          payload={mapPayload}
        />
        {quickReportStage && taskDetail && (
          <DriverDeliveryReportWizard
            detail={taskDetail}
            initialStage={quickReportStage}
            onClose={() => setQuickReportStage(null)}
            onSaved={(options) => refreshSelectedTask(options)}
          />
        )}
      </>
    );
  }

  return (
    <div className="driver-app-shell">
      <a className="skip-link" href="#driver-content">Lewati ke konten utama</a>

      <DriverHeader
        detail={taskDetail}
        hasDetail={Boolean(selectedTaskId)}
        onBack={closeTask}
        onShareReport={selectedTaskId && taskDetail?.deliveryReporting?.available ? openQuickReport : null}
        online={online}
        syncState={syncState}
      />

      <main className="driver-content" id="driver-content" tabIndex="-1">
        <div className={`driver-system-banner-stack${updateRegistration ? " has-update" : ""}`} ref={systemBannerStackRef}>
          {updateRegistration && (
            <section aria-live="assertive" className="driver-update-banner" data-priority="100">
              <Icon name="refresh" size={18} />
              <div><strong>Pembaruan Sedulur Dalan tersedia</strong><small>Perbarui aplikasi agar perbaikan terbaru langsung digunakan. Data offline tetap aman.</small></div>
              <button onClick={() => activateDriverPwaUpdate(updateRegistration)} type="button">Perbarui sekarang</button>
            </section>
          )}
          <DriverSyncRecovery online={online} onChanged={refreshSyncStatus} repairContext={activeSyncRepairContext} status={syncState} syncing={syncState.syncing} />
        </div>

        {selectedTaskId ? (
          <DriverTaskDetail
            actionBusy={actionBusy}
            currentUser={user}
            detail={taskDetail}
            error={detailError}
            loading={detailLoading}
            mapLoading={mapLoading}
            routeWorkspaceLoading={routeWorkspaceLoading}
            onCompleteStop={driverActions.completeStop}
            onConfirmLoading={driverActions.confirmLoading}
            onContinueTrip={driverActions.continueTrip}
            onAcknowledgeAmendment={driverActions.acknowledgeAmendment}
            onFailStop={driverActions.failStop}
            onOpenMap={openMap}
            onOpenRouteWorkspace={openRouteWorkspace}
            onMakeNext={driverActions.makeNext}
            onSaveLoading={driverActions.saveLoading}
            onStartStop={driverActions.startStop}
            onOperationalChanged={(options) => refreshSelectedTask(options)}
            onRetryDetail={() => openTask(selectedTaskId, { updateHistory: false })}
            unsyncedCount={syncState.pending + syncState.failed + syncState.mediaPending + syncState.mediaFailed}
          />
        ) : activePage === "tasks" ? (
          <TasksPage
            error={tasksError}
            loading={tasksLoading}
            onOpenTask={openTask}
            onRefresh={loadTasks}
            tasks={tasks}
          />
        ) : activePage === "profile" ? (
          <ProfilePage onLogout={handleLogout} user={user} />
        ) : activePage === "expenses" ? (
          <DriverExpensesPage currentUser={user} tasks={tasks} />
        ) : activePage === "vehicles" ? (
          <DriverVehiclePage currentUser={user} tasks={tasks} />
        ) : activePage === "notifications" ? (
          <DriverNotificationsPage />
        ) : (
          <PlaceholderPage page={activePage} />
        )}
      </main>


      {quickReportStage && taskDetail && (
        <DriverDeliveryReportWizard
          detail={taskDetail}
          initialStage={quickReportStage}
          onClose={() => setQuickReportStage(null)}
          onSaved={(options) => refreshSelectedTask(options)}
        />
      )}

      {!selectedTaskId && (
        <nav aria-label="Navigasi Sedulur Dalan" className="driver-bottom-nav">
          {DRIVER_NAVIGATION.map((item) => (
            <button
              aria-current={item.id === activePage ? "page" : undefined}
              className={item.id === activePage ? "is-active" : ""}
              key={item.id}
              onClick={() => navigate(item.id)}
              type="button"
            >
              <Icon name={item.icon} size={20} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

function DriverLogin({ onLogin }) {
  const [form, setForm] = useState({ username: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const payload = await loginDriver({
        ...form,
        deviceName: navigator.userAgentData?.platform || navigator.platform || "Perangkat driver",
      });
      onLogin(payload.user);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="driver-login-shell">
      <main className="driver-login-card">
        <div className="driver-login-brand">
          <span><img alt="" src="/brand/route-planning-navbar-64.png" /></span>
          <div>
            <h1>Sedulur Dalan</h1>
            <p>Driver Operations</p>
          </div>
        </div>

        <div className="driver-login-copy">
          <h2>Masuk ke akun Anda</h2>
          <p>Gunakan username dan PIN yang dibuat oleh admin operasional.</p>
        </div>

        {error && <div className="driver-error-banner">{error}</div>}

        <form className="driver-login-form" onSubmit={submit}>
          <label>
            <span>Username</span>
            <input
              autoCapitalize="none"
              autoComplete="username"
              onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))}
              placeholder="Nomor HP atau username"
              required
              value={form.username}
            />
          </label>
          <label>
            <span>PIN 6 digit</span>
            <input
              autoComplete="current-password"
              inputMode="numeric"
              maxLength={6}
              minLength={6}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  pin: event.target.value.replace(/\D/g, "").slice(0, 6),
                }))
              }
              pattern="\d{6}"
              placeholder="••••••"
              required
              type="password"
              value={form.pin}
            />
          </label>
          <button className="driver-primary-button" disabled={busy} type="submit">
            {busy ? "Memeriksa akun..." : "Masuk"}
          </button>
        </form>

        <small className="driver-login-help">Hubungi admin operasional jika belum memiliki akun atau lupa PIN.</small>
      </main>
    </div>
  );
}

function driverDetailSubtitle(detail) {
  const phase = String(detail?.execution?.phase || "").toUpperCase();
  if (new Set(["PREPARATION", "LOADING", "READY_TO_DEPART"]).has(phase)) return "Persiapan perjalanan";
  if (phase === "IN_PROGRESS") return "Perjalanan & pengiriman";
  if (new Set(["RETURNING", "POST_TRIP_CHECK"]).has(phase)) return "Penutupan perjalanan";
  if (phase === "CLOSED") return "Ringkasan perjalanan";
  return "Fleet Order driver";
}

function DriverHeader({ detail, hasDetail, onBack, onShareReport, online, syncState }) {
  return (
    <header className="driver-header">
      <div className="driver-header__brand">
        {hasDetail ? (
          <button aria-label="Kembali ke daftar tugas" onClick={onBack} type="button">
            <Icon name="chevronLeft" size={22} />
          </button>
        ) : (
          <span className="driver-header__logo" aria-hidden="true">
            <img src="/brand/route-planning-navbar-64.png" alt="" />
          </span>
        )}
        <span>
          <strong>{hasDetail ? "Detail Fleet Order" : "Sedulur Dalan"}</strong>
          <small>{hasDetail ? driverDetailSubtitle(detail) : "Driver Operations"}</small>
        </span>
      </div>
      <div className="driver-header__actions">
        {onShareReport && (
          <button
            aria-label="Bagikan laporan perjalanan"
            className="driver-header-share-button"
            onClick={onShareReport}
            type="button"
          >
            <Icon name="upload" size={20} />
          </button>
        )}
        <span className={`driver-online-status ${online ? "" : "is-offline"}`}>
          <span aria-hidden="true" />
          {syncState.syncing
            ? "Menyinkronkan"
            : !online
              ? "Offline"
              : (syncState.pending + syncState.failed + syncState.mediaPending + syncState.mediaFailed) > 0
                ? `${syncState.pending + syncState.failed + syncState.mediaPending + syncState.mediaFailed} belum sinkron`
                : "Online"}
        </span>
      </div>
    </header>
  );
}

function TasksPage({ error, loading, onOpenTask, onRefresh, tasks }) {
  const counts = useMemo(
    () => tasks.reduce(
      (result, task) => ({ ...result, [task.group]: (result[task.group] || 0) + 1 }),
      { running: 0, upcoming: 0, completed: 0 },
    ),
    [tasks],
  );
  const [activeTab, setActiveTab] = useState("running");

  useEffect(() => {
    if (activeTab === "running" && counts.running === 0 && counts.upcoming > 0) {
      setActiveTab("upcoming");
    }
  }, [activeTab, counts.running, counts.upcoming]);

  const visibleTasks = tasks.filter((task) => task.group === activeTab);

  return (
    <div className="driver-page driver-tasks-page">
      <section className="driver-page-heading">
        <div>
          <span className="driver-eyebrow">{todayLabel()}</span>
          <h1>Tugas Saya</h1>
          <p>Fleet Order asli yang ditugaskan kepada akun Anda.</p>
        </div>
        <button className="driver-icon-button" aria-label="Perbarui tugas" disabled={loading} onClick={onRefresh} type="button">
          <Icon name="refresh" size={19} />
        </button>
      </section>

      {error && <div className="driver-error-banner">{error}</div>}
      <DriverIncidentSummary tasks={tasks} />

      <div aria-label="Kategori Fleet Order" className="driver-task-tabs" role="tablist">
        {TASK_TABS.map((tab) => (
          <button
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? "is-active" : ""}
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            type="button"
          >
            <span>{tab.label}</span><strong>{counts[tab.id]}</strong>
          </button>
        ))}
      </div>

      <section className="driver-task-panel" role="tabpanel">
        <div className="driver-task-panel__heading">
          <div>
            <h2>{TASK_TABS.find((tab) => tab.id === activeTab)?.label}</h2>
            <p>{counts[activeTab]} Fleet Order</p>
          </div>
        </div>

        {loading ? (
          <DriverInlineLoading label="Memuat tugas..." />
        ) : visibleTasks.length ? (
          <div className="driver-task-list">
            {visibleTasks.map((task) => (
              <TaskCard
                featured={task.group === "running"}
                key={task.id}
                onOpen={() => onOpenTask(task.id)}
                task={task}
              />
            ))}
          </div>
        ) : (
          <div className="driver-task-empty">
            <span className="driver-placeholder-icon"><Icon name="orders" size={27} /></span>
            <strong>Belum ada Fleet Order</strong>
            <p>Tugas pada kategori ini akan muncul setelah admin menugaskannya.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function TaskCard({ featured, onOpen, task }) {
  const percentage = task.totalStops
    ? Math.round((task.completedStops / task.totalStops) * 100)
    : 0;

  return (
    <article className={`driver-task-card ${featured ? "is-featured" : ""}`}>
      <header>
        <div>
          <span className={`driver-status-chip is-${statusTone(task.status)}`}>{task.statusLabel}</span>
          <h3>{task.foNumber}</h3>
          <p>{task.routeName}</p>
        </div>
        <button aria-label={`Buka ${task.foNumber}`} onClick={onOpen} type="button">
          <Icon name="chevronRight" size={21} />
        </button>
      </header>

      <dl className="driver-task-meta">
        <div><dt>Berangkat</dt><dd>{task.departureDateLabel || task.departureDate || "—"}</dd></div>
        <div><dt>Depot asal</dt><dd>{task.depotName || "Depot belum dipilih"}</dd></div>
        <div><dt>Estimasi pulang</dt><dd>{task.estimatedReturnDateLabel || task.estimatedReturnDate || "—"}</dd></div>
        <div><dt>Armada</dt><dd>{vehicleLabel(task)}</dd></div>
        <div><dt>Co-driver</dt><dd>{task.coDriverName || "Tanpa co-driver"}</dd></div>
      </dl>

      <div className="driver-progress-block">
        <div><span>Progress titik</span><strong>{task.completedStops} / {task.totalStops}</strong></div>
        <div className="driver-progress-track" aria-label={`Progress ${percentage}%`}><span style={{ width: `${percentage}%` }} /></div>
        <small>{task.phaseLabel}</small>
      </div>

      <button className="driver-primary-button" onClick={onOpen} type="button">
        {task.group === "completed" ? "Lihat ringkasan" : "Lihat tugas"}
        <Icon name="chevronRight" size={18} />
      </button>
    </article>
  );
}

function ProfilePage({ onLogout, user }) {
  return (
    <div className="driver-page driver-profile-page">
      <section className="driver-profile-card">
        <span className="driver-placeholder-icon"><Icon name="user" size={30} /></span>
        <h1>{user.displayName}</h1>
        <p>{user.principalType === "DRIVER" ? "Driver" : "Co-driver"} · @{user.username}</p>
      </section>
      <button className="driver-danger-button" onClick={onLogout} type="button">Keluar dari akun</button>
    </div>
  );
}

function PlaceholderPage({ page }) {
  const content = {
    expenses: { icon: "wallet", title: "Pengeluaran", description: "Pencatatan BBM, makan, parkir, hotel, bongkar, dan biaya perjalanan akan tersedia pada tahap berikutnya." },
    vehicles: { icon: "truck", title: "Aktivitas Armada", description: "Vehicle inspection, odometer, dan pencucian armada akan tersedia pada tahap berikutnya." },
    notifications: { icon: "bell", title: "Notifikasi", description: "Perubahan FO, instruksi admin, dan status sinkronisasi akan muncul di halaman ini." },
  }[page];

  return (
    <div className="driver-page driver-placeholder-page">
      <span className="driver-placeholder-icon"><Icon name={content.icon} size={30} /></span>
      <h1>{content.title}</h1><p>{content.description}</p>
      <span className="driver-status-chip is-neutral">Tahap berikutnya</span>
    </div>
  );
}

function DriverInlineLoading({ label }) {
  return <div className="driver-inline-loading"><span className="loading-spinner" /> {label}</div>;
}

function DriverLoadingScreen() {
  return <div className="driver-fullscreen-state"><span className="loading-spinner" /><strong>Memuat Sedulur Dalan...</strong></div>;
}

function DriverFatalScreen({ onRetry }) {
  return (
    <div className="driver-fullscreen-state">
      <Icon name="warning" size={30} />
      <strong>Sedulur Dalan tidak dapat dibuka</strong>
      <p>Periksa koneksi. Data yang belum tersinkron tetap aman di perangkat.</p>
      <button className="driver-primary-button" onClick={onRetry} type="button">Coba lagi</button>
    </div>
  );
}
